import Phaser from 'phaser';
import { MOD_TEXT } from '../../content/mods';
import { MOON, SOL, isBossRoom, planetRoom, type Planet } from '../../content/planets';
import { JUNGLE_TEST } from '../../content/rooms/jungleTest';
import { RANGER_LOG } from '../../content/story';
import { EARTH_TUTORIAL, tutorialTip } from '../../content/tutorial';
import { SEEN, finishTutorial, landedOn, readRangerLog, see } from '../../meta/progress';
import { BOSS_MATERIALS, suitStats } from '../../meta/upgrades';
import { TILE } from '../../sim/constants';
import { parseRoom, type RoomDef } from '../../sim/room';
import { Simulation, type SimEvent } from '../../sim/simulation';
import { TUNING } from '../../sim/tuning';
import { Sfx, type Ambience } from '../audio/Sfx';
import { FixedStepper } from '../fixedStep';
import { InputController } from '../input/InputController';
import { BIOMES, type BiomeId } from '../render/biomes';
import { WorldView, createWorldTextures } from '../render/WorldView';
import { afterRoom, newSeed, nextRun, type RunState, type SceneKey } from '../run';
import { saveStore } from '../storage';
import { Hud, roomHudState } from '../ui/Hud';
import { PauseMenu } from '../ui/PauseMenu';
import { CELL_W, PIXEL_FONT } from '../ui/pixelFont';

/** How long the suit's first-time words (a landing, radiation, a mod) stay up. */
const TIP_MS = 7000;

/**
 * What the suit says the first time it meets radiation, or a mod it has no slot for (the rate is
 * TUNING.radiationTicks: keep the words in step).
 */
const WORDS = {
  radiation:
    'RADIATION ABOVE OUR SHIELDING: IT BURNS A PLATE A SECOND. SHIELDING IN THE LAB MAKES IT SAFE.',
  noSlot:
    'A WEAPON MOD, BUT NO SLOT TO FIT IT. ARSENAL IN THE LAB ADDS ONE. BROKEN DOWN FOR A MATERIAL.',
} as const;

/** Each world's background sound. */
const AMBIENCE: Readonly<Record<BiomeId, Ambience>> = {
  earth: 'crickets',
  moon: 'void',
  mars: 'wind',
  jungle: 'forest',
};

/**
 * One room of a run: the Earth tutorial, or a room of a planet (its seeded rooms, then its boss's
 * arena). Steps the simulation at a fixed rate, feeds it the player's intents, and hands its
 * events to the view and the sounds. Leaving a room goes where `afterRoom` says: the next room,
 * the transit to the next planet, or back to the lab; so does a rewind (the roguelite loop).
 * In dev builds `?room=earth` plays the tutorial, `?room=boss` the Ranger's arena, `?room=test`
 * the hand-made jungle test room, `?planet=mars` starts on Mars, and `?seed=abc12` replays a run.
 */
export class PlayScene extends Phaser.Scene {
  sim!: Simulation;
  run!: RunState;
  private view!: WorldView;
  pause!: PauseMenu;
  private hud!: Hud;
  private controls!: InputController;
  private sfx!: Sfx;
  private readonly stepper = new FixedStepper();
  /** Set while fading out to the next room: the sim stops stepping. */
  private leaving = false;
  /** The farthest tile column reached (the tutorial's tips follow it). */
  private farthest = 0;
  /** The first-time words up now, if any (cleared after TIP_MS unless replaced). */
  private words: string | null = null;

  constructor() {
    super('Play');
  }

  create(data: Partial<RunState>): void {
    const params = new URLSearchParams(window.location.search);
    // Room shortcuts are for development: they would let a release skip to the boss's reward.
    // They apply when the scene is opened from boot, not to the rooms that follow.
    const dev = import.meta.env.DEV && data.seed === undefined;
    const roomOption = dev ? params.get('room') : null;
    const bossPlanet = Math.max(
      0,
      SOL.findIndex((p) => p.boss !== null),
    );
    const devPlanet = dev
      ? Math.max(
          0,
          SOL.findIndex((p) => p.id === params.get('planet')),
        )
      : 0;
    const planetIndex = data.planet ?? (roomOption === 'boss' ? bossPlanet : devPlanet);
    this.run = {
      seed: data.seed ?? params.get('seed') ?? newSeed(),
      planet: planetIndex,
      room: data.room ?? (roomOption === 'boss' ? (SOL[planetIndex]?.rooms ?? 0) : 0),
      rewinds: data.rewinds ?? 0,
      tutorial: data.tutorial ?? roomOption === 'earth',
      carry: data.carry,
    };
    const planet = SOL[this.run.planet] ?? MOON;
    let def: RoomDef;
    let biome: BiomeId;
    let label: string;
    if (this.run.tutorial) {
      def = EARTH_TUTORIAL;
      biome = 'earth';
      label = 'EARTH';
    } else if (roomOption === 'test') {
      def = JUNGLE_TEST;
      biome = 'jungle';
      label = 'TEST ROOM';
    } else {
      def = planetRoom(planet, this.run.seed, this.run.room);
      biome = planet.id;
      label = isBossRoom(planet, this.run.room)
        ? `${planet.name} BOSS`
        : `${planet.name} ${this.run.room + 1}/${planet.rooms}`;
    }
    this.sim = new Simulation(parseRoom(def), this.run.carry, suitStats(saveStore().save.upgrades));
    this.sim.rewinds = this.run.rewinds;
    this.leaving = false;
    this.farthest = 0;
    this.words = null;
    this.stepper.reset();

    createWorldTextures(this, this.sim, BIOMES[biome]);
    this.view = new WorldView(this, this.sim, BIOMES[biome]);
    this.hud = new Hud(this);
    this.hud.setRoom(label, this.run.seed);
    if (!this.run.tutorial && def !== JUNGLE_TEST && this.run.room === 0) this.introduce(planet);
    this.controls = new InputController(this);
    this.sfx = new Sfx(this);
    this.pause = new PauseMenu(this, {
      onRewind: () => {
        if (!this.leaving) this.rewindToLab(this.sim.rewinds + 1);
      },
      onChange: (paused) => this.controls.setEnabled(!paused),
    });
    const firstRoom = this.run.planet === 0 && this.run.room === 0 && this.run.rewinds === 0;
    if (firstRoom && !this.run.tutorial) this.showKeyboardHint();
    this.cameras.main.fadeIn(250, 13, 10, 31);

    // Audio may only start after a gesture (the ambience carries on across rooms).
    const startAudio = () => this.sfx.startAmbience(AMBIENCE[biome]);
    if (this.sound.locked) {
      this.input.once('pointerdown', startAudio);
      this.input.keyboard?.once('keydown', startAudio);
    } else {
      startAudio();
    }

    const onResize = (size: Phaser.Structs.Size) => {
      this.view.resize(size.width, size.height);
      this.hud.resize();
      this.pause.resize();
    };
    this.scale.on('resize', onResize);
    this.events.once('shutdown', () => {
      this.scale.off('resize', onResize);
      this.sfx.stopAll();
    });
  }

  override update(_time: number, delta: number): void {
    if (!this.leaving && !this.pause.paused) {
      const steps = this.stepper.advance(delta);
      for (let i = 0; i < steps && !this.leaving; i++) {
        this.view.capture();
        const events = this.sim.step(this.controls.sample());
        this.react(events);
      }
    }
    if (!this.pause.paused) {
      this.sfx.geiger(delta / 1000, this.sim.player.exposed && !this.leaving);
    }
    this.view.render(delta / 1000, this.stepper.alpha);
    this.controls.touch.draw();
    if (this.run.tutorial) this.updateTip();
    this.hud.update(roomHudState(this.sim, saveStore().save.materials), this.game.loop.actualFps);
  }

  private react(events: readonly SimEvent[]): void {
    this.sfx.play(events);
    for (const e of events) {
      if (e.type === 'laneBlocked') this.view.bump(e.to > this.sim.player.lane);
      else if (e.type === 'enemyKilled') {
        const enemy = this.sim.enemies.find((en) => en.id === e.id);
        if (enemy)
          this.view.burst(enemy.lane, enemy.x + enemy.w / 2, enemy.y + enemy.h / 2, '#7dffb0');
      } else if (e.type === 'shard') {
        // Materials go straight into the save: they are kept through a rewind.
        saveStore().update((s) => {
          s.materials += 1;
        });
        const p = this.sim.player;
        this.view.burst(p.lane, p.x + p.w / 2, p.y + p.h / 2, '#e8c8ff');
      } else if (e.type === 'pickup') {
        const p = this.sim.player;
        this.view.burst(p.lane, p.x + p.w / 2, p.y + p.h / 2, '#ffd23f');
      } else if (e.type === 'radiation') {
        this.sayOnce(SEEN.radiation, WORDS.radiation);
      } else if (e.type === 'modFitted') {
        const mod = MOD_TEXT[e.mod];
        this.hud.flash(mod.name);
        this.sayOnce(SEEN.modFitted, `${mod.name}: ${mod.does}. IT LASTS THE RUN.`);
        const p = this.sim.player;
        this.view.burst(p.lane, p.x + p.w / 2, p.y + p.h / 2, '#7df9ff');
      } else if (e.type === 'modSalvaged') {
        // A 'shard' event follows: the material itself is counted there.
        const slots = this.sim.suit.modSlots;
        this.hud.flash(
          e.mod === null ? 'ALL MODS FITTED' : slots === 0 ? 'NO MOD SLOT' : 'SLOTS FULL',
        );
        if (slots === 0) this.sayOnce(SEEN.modNoSlot, WORDS.noSlot);
      } else if (e.type === 'blast') {
        this.view.blast(e.lane, e.x, e.y, TUNING.blastRadius);
        this.cameras.main.shake(100, 0.004);
      } else if (e.type === 'hurt') {
        this.cameras.main.shake(120, 0.006);
      } else if (e.type === 'enemyBlocked') {
        const enemy = this.sim.enemies.find((en) => en.id === e.id);
        if (enemy) this.view.burst(enemy.lane, enemy.x + enemy.w / 2, enemy.y + 8, '#e8fff0');
      } else if (e.type === 'bossHop') {
        this.view.burst(e.from, e.fromX, e.fromY, '#ff2a4a');
        this.view.burst(e.to, e.toX, e.toY, '#ff2a4a');
      } else if (e.type === 'bossSlam') {
        this.cameras.main.shake(160, 0.008);
      } else if (e.type === 'bossPhase') {
        this.cameras.main.flash(250, 255, 60, 60);
      } else if (e.type === 'bossDefeated') {
        saveStore().update((s) => {
          s.materials += BOSS_MATERIALS;
          s.stats.bossesBeaten += 1;
          readRangerLog(s);
        });
        this.hud.flash(`RANGER DOWN +${BOSS_MATERIALS}`);
        this.hud.showLog(RANGER_LOG);
        this.cameras.main.shake(300, 0.01);
      } else if (e.type === 'rewind') {
        this.rewindToLab(this.sim.rewinds);
      } else if (e.type === 'roomExit') {
        this.exitRoom();
      }
    }
  }

  private exitRoom(): void {
    const next = afterRoom(this.run, this.sim.carry());
    if (this.run.tutorial) {
      saveStore().update(finishTutorial);
      this.hud.flash('HEAT SHIELD ON');
      this.leave(next.scene, next.run, 1200);
    } else if (next.scene === 'Play') {
      this.leave(next.scene, next.run, 0);
    } else {
      this.hud.flash(next.scene === 'Lab' ? 'SOL CLEAR' : 'PLANET CLEAR');
      this.leave(next.scene, next.run, 1200);
    }
  }

  /**
   * The suit pulls the player back through time to the lab: a new run, a new seed. After the last
   * plate is lost, or when the player gives the run up from the pause menu.
   */
  private rewindToLab(rewinds: number): void {
    saveStore().update((s) => {
      s.stats.rewinds += 1;
    });
    this.hud.flash('REWIND');
    this.cameras.main.flash(400, 255, 255, 255);
    this.leave('Lab', nextRun(rewinds), 900);
  }

  /** Fades out, then starts the next scene (restarting this one for the next room). */
  private leave(scene: SceneKey, next: RunState, delayMs: number): void {
    if (this.leaving) return;
    this.leaving = true;
    this.time.delayedCall(delayMs, () => {
      this.cameras.main.fadeOut(250, 13, 10, 31);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        if (scene === 'Play') this.scene.restart(next);
        else this.scene.start(scene, next);
      });
    });
  }

  /** The first landing on a planet: the suit says what is new here, once. */
  private introduce(planet: Planet): void {
    this.sayOnce(landedOn(planet.id), planet.intro);
  }

  /** The suit's words for a story beat, the first time it is reached in this save. */
  private sayOnce(beat: string, text: string): void {
    // Most calls are for beats already seen: don't write the save for those.
    if (saveStore().save.seen.includes(beat)) return;
    let first = false;
    saveStore().update((s) => {
      first = see(s, beat);
    });
    if (!first) return;
    this.words = text;
    this.hud.setTip(text);
    this.time.delayedCall(TIP_MS, () => {
      if (this.words !== text) return;
      this.words = null;
      this.hud.setTip(null);
    });
  }

  /** The tutorial's tip for how far the player has got, in words for touch or for keys. */
  private updateTip(): void {
    this.farthest = Math.max(this.farthest, Math.floor(this.sim.player.x / TILE));
    const tip = tutorialTip(this.farthest);
    const touch = this.sys.game.device.input.touch;
    this.hud.setTip(tip ? (touch ? tip.touch : tip.keys) : null);
  }

  /** On a desktop browser (no touch), show the keys once. */
  private showKeyboardHint(): void {
    if (this.sys.game.device.input.touch) return;
    const hint = this.add
      .bitmapText(
        Math.round(this.scale.width / 2),
        this.scale.height - 14,
        PIXEL_FONT,
        'ARROWS MOVE  SPACE JUMP/JET  J FIRE (HOLD: ROCKET)  SHIFT DASH  Q/E LANE',
        CELL_W,
      )
      .setOrigin(0.5)
      .setDepth(950);
    this.tweens.add({ targets: hint, alpha: 0, delay: 8000, duration: 1500 });
  }
}
